/**
 * lib/sessionBridge.js — Ponte entre Supabase Auth e o `app_session` legacy.
 *
 * O TenantContext hidrata a partir de LocalStorage `app_session` no shape:
 *   {
 *     user_id, user_nome, user_email, user_papel,
 *     workspace_id, workspace_nome, empresa_perfil_id,
 *     has_v360, has_sprints360, has_custom_metrics
 *   }
 *
 * Este módulo:
 *   1. Escuta `supabase.auth.onAuthStateChange`
 *   2. Ao SIGNED_IN: puxa metadados de /api/auth/me + /api/me/plano-caps
 *      e escreve `app_session` no shape esperado
 *   3. Ao SIGNED_OUT: limpa `app_session`
 *
 * Chamar `initSessionBridge()` uma vez, no topo do App (App.js/index.js).
 */
import { getSupabase } from "./supabase";

const LS_KEY = "app_session";

async function fetchMe(accessToken) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const r = await fetch(`${base}/api/auth/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!r.ok) throw new Error(`GET /api/auth/me: ${r.status}`);
  return r.json();
}

async function fetchPlanoCaps(workspaceId, accessToken) {
  // Phase 12.3 — Endpoint real que lê `Workspace.plano` e reconhece tanto
  // SKUs legados (`adm.planos`) quanto os tiers novos S360/V360 (Phase 11.12+).
  // Fallback aberto em caso de erro (workspace mock ou rede fora).
  if (!workspaceId) return { has_v360: true, has_sprints360: true, has_custom_metrics: true, plano_customizado: false };
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    const r = await fetch(`${base}/api/auth/plano-caps`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!r.ok) throw new Error(`plano-caps HTTP ${r.status}`);
    const d = await r.json();
    return {
      has_v360: d.has_v360 !== false,
      has_sprints360: d.has_sprints360 !== false,
      has_custom_metrics: d.has_custom_metrics !== false,
      plano_customizado: d.plano_customizado === true,
      plano_codigo: d.plano_codigo || null,
    };
  } catch (e) {
    console.warn("[sessionBridge] plano-caps falhou, usando defaults abertos:", e);
    return { has_v360: true, has_sprints360: true, has_custom_metrics: true, plano_customizado: false, plano_codigo: null };
  }
}

function papelLabel(papel, isSuperAdm) {
  if (isSuperAdm) return "Super ADM";
  const map = {
    super_adm_empresa: "Adm. Master",
    adm_empresa: "Administrador",
    executivo: "Executivo",
    consultor: "Consultor",
    gp: "Gerente de Projeto",
    gerente_projeto: "Gerente de Projeto",
    executor: "Executor",
  };
  return map[papel] || papel || "Membro";
}

async function buildLegacySession(session) {
  const token = session?.access_token;
  if (!token) return null;
  const me = await fetchMe(token);
  const caps = await fetchPlanoCaps(me.workspace_id, token);

  // O nome do usuário e o papel vêm do adm.usuarios via view derivada.
  // Para evitar mais uma requisição por hora, guardamos o e-mail e o "papel"
  // no user_metadata do JWT (fallback ao email). Fase C vai enriquecer.
  const nome = session.user?.user_metadata?.nome || (me.email || "").split("@")[0];
  const papelRaw = session.user?.user_metadata?.papel || (me.is_super_adm ? "super_adm" : "super_adm_empresa");

  return {
    user_id: me.user_id,
    user_nome: nome,
    user_email: me.email,
    user_papel: papelRaw,
    user_papel_label: papelLabel(papelRaw, me.is_super_adm),
    is_super_adm: !!me.is_super_adm,
    senha_provisoria: !!me.senha_provisoria,
    // Fev/2026 — permissão POR USUÁRIO aos módulos (não confundir com
    // has_v360/has_sprints360, que é por EMPRESA/plano). Default aberto.
    acesso_gestao: me.acesso_gestao !== false,
    acesso_projetos: me.acesso_projetos !== false,
    workspace_id: me.workspace_id || null,
    empresa_perfil_id: me.empresa_id || null,
    workspace_nome: me.workspace_nome || null,   // Phase 12.4 — vem direto do /me
    workspace_status: me.workspace_status || "ativo", // Phase 12.30 — status para bloqueio de navegação
    workspace_ativo: me.workspace_ativo !== false,
    idioma_padrao: me.idioma_padrao || "pt-BR",  // Phase 12.31 — derivado do país
    has_v360: caps.has_v360,
    has_sprints360: caps.has_sprints360,
    has_custom_metrics: caps.has_custom_metrics,
    plano_customizado: caps.plano_customizado === true,   // Plano Customizado · E1
    plano_codigo: caps.plano_codigo || null,   // Phase 12.27 · plano CONSULTORIA
    _supabase: true,
    _issued_at: Date.now(),
  };
}

function writeLegacySession(payload) {
  try {
    if (payload) {
      localStorage.setItem(LS_KEY, JSON.stringify(payload));
    } else {
      localStorage.removeItem(LS_KEY);
    }
    window.dispatchEvent(new CustomEvent("app-session-changed"));
  } catch { /* Safari private mode etc. */ }
}

let _initialized = false;
export async function initSessionBridge() {
  if (_initialized) return;
  _initialized = true;
  const sb = await getSupabase();

  // 1) Bootstrap sync da sessão persistida
  const { data } = await sb.auth.getSession();
  if (data.session) {
    try {
      const legacy = await buildLegacySession(data.session);
      writeLegacySession(legacy);
    } catch (e) {
      console.warn("[sessionBridge] falha ao hidratar sessão inicial", e);
    }
  }

  // 2) Listener de mudanças
  sb.auth.onAuthStateChange(async (event, session) => {
    if (event === "SIGNED_OUT") {
      writeLegacySession(null);
      return;
    }
    if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
      try {
        const legacy = await buildLegacySession(session);
        writeLegacySession(legacy);
      } catch (e) {
        console.warn(`[sessionBridge] falha em ${event}`, e);
      }
    }
  });
}

/**
 * refreshPlanoCaps — re-consulta `/api/auth/plano-caps` e mescla o resultado
 * no `app_session` existente. Chamado após ações comerciais (ex.: "Adquirir
 * V360" em /app/subscricoes) para que a UI reflita imediatamente os novos
 * módulos habilitados, sem exigir logout/login.
 *
 * Dispara `app-session-changed` no window para o TenantContext re-hidratar.
 * Retorna as caps atualizadas ou null se falhar.
 */
export async function refreshPlanoCaps() {
  try {
    // Usa getAccessToken (com fallback refreshSession) em vez de getSession()
    // direto — evita retornar null em janelas long-idle.
    const { getAccessToken } = await import("./supabase");
    const token = await getAccessToken();
    if (!token) return null;
    const me = await fetchMe(token);
    const caps = await fetchPlanoCaps(me.workspace_id, token);
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return caps;
    const prev = JSON.parse(raw);
    const next = {
      ...prev,
      has_v360: caps.has_v360,
      has_sprints360: caps.has_sprints360,
      has_custom_metrics: caps.has_custom_metrics,
      plano_customizado: caps.plano_customizado === true,
      plano_codigo: caps.plano_codigo || prev.plano_codigo,
    };
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("app-session-changed"));
    return caps;
  } catch (e) {
    console.warn("[sessionBridge] refreshPlanoCaps falhou:", e);
    return null;
  }
}
